import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/90 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
      style={{ height: '72px' }}
    >
      <div className="w-full h-full flex items-center justify-between px-6 md:px-10">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex flex-col">
            <span className="text-lg font-bold text-foreground-950 whitespace-nowrap font-heading tracking-tight">
              hipobuyspread<span className="text-accent-500">.hair</span>
            </span>
            <span className="text-[10px] text-foreground-500 font-body italic tracking-wide">
              spreadsheet resource hub
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3" ref={dropdownRef}>
          <div className="relative hidden md:block">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>All Categories</span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-[640px] bg-background-50 rounded-2xl border border-background-200 shadow-lg p-6 z-50">
                <div className="grid grid-cols-4 gap-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors text-sm text-foreground-700 hover:text-foreground-950 cursor-pointer group"
                    >
                      <span className="whitespace-nowrap">{cat.name}</span>
                      <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span>hipobuy sheet</span>
            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-background-50/20">
              <i className="ri-arrow-right-up-line text-xs"></i>
            </span>
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-full border border-foreground-300 text-foreground-900 text-sm font-medium hover:bg-foreground-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span>buy it now</span>
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-background-100 border border-background-200 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="fixed inset-0 top-[72px] z-40 md:hidden" ref={mobileMenuRef}>
          <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute top-0 right-0 w-[85%] max-w-[380px] h-full bg-background-50 overflow-y-auto shadow-xl">
            <div className="p-5">
              <div className="flex flex-col gap-2 mb-6">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-between px-4 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <span>hipobuy sheet</span>
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-50/20">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-between px-4 py-3 rounded-xl border border-foreground-300 text-foreground-900 text-sm font-semibold hover:bg-foreground-50 transition-colors cursor-pointer"
                >
                  <span>buy it now</span>
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </a>
              </div>

              <div className="mb-4">
                <h3 className="text-xs font-semibold text-foreground-400 font-body tracking-wider uppercase mb-3">All Categories</h3>
                <div className="grid grid-cols-2 gap-1.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                        cat.color === 'accent'
                          ? 'bg-accent-50 text-accent-800 hover:bg-accent-100 active:bg-accent-200'
                          : 'bg-secondary-50 text-secondary-800 hover:bg-secondary-100 active:bg-secondary-200'
                      }`}
                    >
                      <span className="whitespace-nowrap text-xs">{cat.name}</span>
                      <i className="ri-arrow-right-s-line text-sm opacity-60"></i>
                    </a>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-background-200">
                <p className="text-[11px] text-foreground-400 font-body text-center">
                  Discover verified products on kaspreadsheet.com
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}